import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect } from "react"
import { adminApi } from "services/adminApi"
import { useAppSelector } from "hooks/storeHooks"
import { selectUserInfo } from "store/user/userSlice"
import { UserStatusTypes } from "types"
import { removeTryOnKey, upsertTryOnKey, type TryOnKeysList } from "./tryOnKeysCache"

export const TRYON_KEYS_KEY = (merchantId?: string) =>
  ["try-on", "publishable-keys", merchantId ?? "self"] as const

/**
 * merchantId is only passed by superadmins viewing another merchant
 * (/integration-info?id=…); merchants always operate on their own scope.
 * `enabled: false` is for superadmins WITHOUT an explicit merchant — the
 * server requires merchantId for admin access and would 400 otherwise.
 */
export const useTryOnKeysQuery = (merchantId?: string, enabled = true) =>
  useQuery({
    queryKey: TRYON_KEYS_KEY(merchantId),
    queryFn: () => adminApi.listTryOnPublishableKeys(merchantId),
    enabled,
    retry: false,
  })

/**
 * Warms the try-on integrations list from the workspace layout: the first GET
 * rides a cold Lambda plus a full authorizer re-run, which QA read as "поля
 * интеграции 1-2 секунды пустые" (Asana 1216941879631471). Same guards as
 * `usePrefetchViewer3DKeys`: admins are skipped (their scope comes from ?id=)
 * and so are non-ACTIVE users — a pre-activation call can get an authorizer
 * Deny cached against the token by API Gateway.
 */
export const usePrefetchTryOnKeys = () => {
  const queryClient = useQueryClient()
  const userInfo = useAppSelector(selectUserInfo)
  const isAdmin = userInfo?.groups.some((group) => group.trim().toLowerCase() === "admin")
  const isActive = userInfo?.status === UserStatusTypes.ACTIVE

  useEffect(() => {
    if (isAdmin || !isActive) return
    void queryClient.prefetchQuery({
      queryKey: TRYON_KEYS_KEY(),
      queryFn: () => adminApi.listTryOnPublishableKeys(),
      staleTime: 60_000,
    })
  }, [queryClient, isAdmin, isActive])
}

/**
 * The mutation response IS the authoritative record (see `tryOnKeysCache.ts`),
 * so write it into the cached list instead of refetching — the follow-up GET
 * rode a cold Lambda and left the saved dialog showing stale rows for ~2s.
 * `cancelQueries` first: an in-flight stale GET must not overwrite the fresher
 * mutation result. Mirrors `useViewer3D.ts`.
 */
const writeTryOnKeysCache = async (
  queryClient: ReturnType<typeof useQueryClient>,
  merchantId: string | undefined,
  write: (previous: TryOnKeysList | undefined) => TryOnKeysList | undefined
) => {
  await queryClient.cancelQueries({ queryKey: TRYON_KEYS_KEY(merchantId) })
  queryClient.setQueryData<TryOnKeysList>(TRYON_KEYS_KEY(merchantId), write)
  queryClient.invalidateQueries({ queryKey: TRYON_KEYS_KEY(merchantId), refetchType: "none" })
}

export const useCreateTryOnKeyMutation = (merchantId?: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { allowedOrigins: string[]; label?: string }) =>
      adminApi.createTryOnPublishableKey({ ...payload, merchantId }),
    onSuccess: (key) =>
      writeTryOnKeysCache(queryClient, merchantId, (previous) => upsertTryOnKey(previous, key)),
  })
}

/** Rename an integration or change its Store URLs — the key never changes. */
export const useUpdateTryOnKeyMutation = (merchantId?: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      keyId,
      ...payload
    }: {
      keyId: string
      allowedOrigins?: string[]
      label?: string
    }) => adminApi.updateTryOnPublishableKey(keyId, { ...payload, merchantId }),
    onSuccess: (key) =>
      writeTryOnKeysCache(queryClient, merchantId, (previous) => upsertTryOnKey(previous, key)),
  })
}

export const useRevokeTryOnKeyMutation = (merchantId?: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (keyId: string) => adminApi.revokeTryOnPublishableKey(keyId, merchantId),
    onSuccess: (_result, keyId) =>
      writeTryOnKeysCache(queryClient, merchantId, (previous) => removeTryOnKey(previous, keyId)),
  })
}
